'use client';

/**
 * Directory — THE INDEX (per-chain entity index table; map-inversion re-cut).
 *
 * The dense, scannable index BENEATH the map (docs/design/map-prototype/map.html):
 * a sticky sub-header (search + type chips + the SIGNAL/A–Z/CONNECTIONS sort control,
 * SIGNAL default) over ~44px rows — avatar · name (gold ● tracked dot) · type chip ·
 * `◆ N` connections · INTEL freshness (recon.generatedAt, relative) · coverage
 * (contentCount stories) · `→`. Replaces the old fat-card grid; the graph above is
 * now the page's visual source of truth and this is its index.
 *
 * SEO-SAFE in-place behavior (founder intent, chat1.md): every row is a crawlable
 * `<a href="/tags/[slug]">` (SSR HTML, middle-clickable, real link) BUT the left-click
 * is intercepted (`preventDefault`) to SELECT the entity into the page's UNIFIED
 * inspector (the EntityGraph rail / phone bottom sheet) — exploring without leaving
 * the index. The deliberate jump to `/tags/[slug]` is the inspector's "View full profile →".
 *
 * SELECTION IS CONTROLLED: the page-level <EntitiesExplorer> owns `selectedSlug` and
 * passes it down with `onSelect` — the same state the graph reads, so graph and index
 * cross-highlight (the active row's inset gold spine ↔ the focused node above).
 *
 * Filtering hides/shows rows; the markup stays in the DOM (cheap re-filter, and the
 * crawlable links are always present).
 */

import { entityUrl } from '@/lib/entity-url';
import { useMemo, useState } from 'react';
import Link from 'next/link';
import { Icon, Avatar, Mono } from './atoms';
import { ENTITY_TYPES, type EntityTypeKey } from './format';
import { getChainLabel } from '@/lib/chains';
import { XIcon } from '@/components/icons/SocialIcons';
import type { ChainEntity, EntitySearchRow } from '@/lib/hub/chain-entities-data';

// The filter row: "all" + the canonical prototype type order. We only RENDER a chip
// for a type that actually appears in this chain's data (derived below).
const TYPE_ORDER: EntityTypeKey[] = ['project', 'game', 'character', 'person', 'organization', 'protocol'];

/** Max cross-chain hint rows shown for a query. */
const CROSS_CHAIN_CAP = 8;

/** Directory sort modes (the mono segmented control). SIGNAL = default. */
type SortMode = 'signal' | 'name' | 'connections';
const SORT_MODES: ReadonlyArray<readonly [SortMode, string]> = [
  ['signal', 'Signal'],
  ['name', 'A–Z'],
  ['connections', 'Connections'],
];

/**
 * SIGNAL ranking — the "map room" default. Descending priority, all over EXISTING
 * projected fields (no new Firestore reads): live activity (`tracked` — the closest
 * recent-activity signal we have) → typed-edge degree (`connectionCount`) → coverage
 * volume (`contentCount` = article + breaking-news counts) → name A–Z stable tiebreak.
 */
function compareSignal(a: ChainEntity, b: ChainEntity): number {
  const at = a.tracked ? 1 : 0;
  const bt = b.tracked ? 1 : 0;
  if (at !== bt) return bt - at;
  const ac = a.connectionCount ?? 0;
  const bc = b.connectionCount ?? 0;
  if (ac !== bc) return bc - ac;
  const av = a.contentCount ?? 0;
  const bv = b.contentCount ?? 0;
  if (av !== bv) return bv - av;
  return a.name.localeCompare(b.name);
}

/** CONNECTIONS sort — typed-edge degree desc, then name A–Z. */
function compareConnections(a: ChainEntity, b: ChainEntity): number {
  const ac = a.connectionCount ?? 0;
  const bc = b.connectionCount ?? 0;
  if (ac !== bc) return bc - ac;
  return a.name.localeCompare(b.name);
}

/**
 * Shared query matcher — name / slug / handle / token symbol (mirrors the global
 * nav search's fields). A leading `$` targets the SYMBOL ("$BURR" → BURR).
 */
function matchesQuery(
  q: string,
  qSym: string,
  e: { name: string; slug: string; handle?: string; symbol?: string },
): boolean {
  return (
    e.name.toLowerCase().includes(q) ||
    e.slug.toLowerCase().includes(q) ||
    (e.handle || '').toLowerCase().includes(q) ||
    (!!e.symbol && !!qSym && e.symbol.toLowerCase().includes(qSym))
  );
}

/**
 * Compact relative-time for the INTEL freshness column (recon.generatedAt).
 * "40m" / "3h" / "2d" / a Mon-D date past a week; em-dash when absent/unparseable.
 */
function intelAgo(iso: string | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso).getTime();
  if (Number.isNaN(d)) return '—';
  const mins = Math.floor((Date.now() - d) / 60000);
  if (mins < 0) return '—';
  if (mins < 60) return `${Math.max(1, mins)}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 48) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/** Human chain label for a cross-chain hint row, e.g. "on Ethereum". */
function whereLabel(row: EntitySearchRow): string {
  const labels = row.chains
    .map((c) => getChainLabel(c) ?? (c ? c.charAt(0).toUpperCase() + c.slice(1) : ''))
    .filter(Boolean);
  if (labels.length === 0) return ENTITY_TYPES[row.type as EntityTypeKey]?.label ?? row.type;
  return `on ${labels.slice(0, 2).join(' · ')}`;
}

export default function Directory({
  entities,
  offChainIndex = [],
  edgesAdded30d,
  selectedSlug = null,
  onSelect,
}: {
  entities: ChainEntity[];
  /** Slim index of ACTIVE entities on OTHER chains — powers the cross-chain search hint. */
  offChainIndex?: EntitySearchRow[];
  /** Per-entity slug → count of typed edges added in the last 30d (from edge
      createdAt). Drives the honest `+N` Δ30d marker next to Connections; absent
      / 0 → no marker (never an invented movement arrow). */
  edgesAdded30d?: Map<string, number>;
  /** CONTROLLED selection (owned by <EntitiesExplorer>) — highlights the active row. */
  selectedSlug?: string | null;
  /** Select an entity into the page's unified inspector. */
  onSelect: (slug: string) => void;
}) {
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | string>('all');
  // Default SIGNAL — the high-signal-first "map room" order (SSR renders this).
  const [sortMode, setSortMode] = useState<SortMode>('signal');

  // Which type chips to show — only types present in the data, in canonical order.
  const availableTypes = useMemo(() => {
    const present = new Set(entities.map((e) => e.type));
    return TYPE_ORDER.filter((t) => present.has(t));
  }, [entities]);

  // Filtered list (search over name/slug/handle/token symbol + type chip).
  // "$BURR" style queries target the token symbol.
  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    const qSym = q.startsWith('$') ? q.slice(1) : q;
    return entities.filter(
      (e) =>
        (typeFilter === 'all' || e.type === typeFilter) &&
        (!q ||
          matchesQuery(q, qSym, {
            name: e.name,
            slug: e.slug,
            handle: e.handle,
            symbol: e.token?.symbol,
          })),
    );
  }, [entities, query, typeFilter]);

  // Cross-chain hint — ACTIVE entities OUTSIDE this chain matching the query.
  // No silent dead-ends: if a project migrated off this chain (correct facet!),
  // searching it still surfaces a dimmed "Other chains" row → /tags/[slug].
  const crossMatches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q || offChainIndex.length === 0) return [];
    const qSym = q.startsWith('$') ? q.slice(1) : q;
    return offChainIndex.filter((r) => matchesQuery(q, qSym, r)).slice(0, CROSS_CHAIN_CAP);
  }, [offChainIndex, query]);

  // ONE flat, sorted list (the index is a single dense table — no per-type groups).
  // SIGNAL default → high-signal entities lead, dormant zero-edge tails sink.
  const sorted = useMemo(() => {
    const cmp =
      sortMode === 'name'
        ? (a: ChainEntity, b: ChainEntity) => a.name.localeCompare(b.name)
        : sortMode === 'connections'
          ? compareConnections
          : compareSignal;
    return [...list].sort(cmp);
  }, [list, sortMode]);

  return (
    <section aria-label="Entity index" className="mt-8">
      {/* sticky sub-header — search · type chips · sort. Sticks under the TopBar
          (top-14 = 56px) so the controls stay reachable while scanning a long index. */}
      <div className="sticky top-14 z-20 -mx-4 mb-1 flex flex-wrap items-center gap-x-3 gap-y-2 border-b-[0.5px] border-divider bg-ink/90 px-4 py-3 backdrop-blur-md sm:mx-0 sm:rounded-t sm:px-4">
        <span className="flex-shrink-0 font-mono text-[10px] uppercase tracking-[0.14em] text-parchment">
          The index
          <span className="ml-2 font-normal text-parchment-dim">{list.length}</span>
        </span>
        <label className="inline-flex min-w-0 flex-1 items-center gap-2 rounded border-[0.5px] border-divider bg-surface px-3 py-1.5 sm:max-w-[280px] sm:flex-initial">
          <span className="text-parchment-dim">
            <Icon name="search" size={14} />
          </span>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search the index…"
            aria-label="Search the entity index"
            className="min-w-0 flex-1 bg-transparent font-display text-[13px] text-parchment outline-none placeholder:text-parchment-dim"
          />
        </label>

        <div className="flex flex-wrap items-center gap-1.5">
          {(['all', ...availableTypes] as const).map((t) => {
            const active = typeFilter === t;
            const label =
              t === 'all' ? 'All' : ENTITY_TYPES[t as EntityTypeKey]?.label ?? t;
            return (
              <button
                key={t}
                type="button"
                onClick={() => setTypeFilter(t)}
                aria-pressed={active}
                className={`cursor-pointer rounded-full border-[0.5px] px-2.5 py-1 font-mono text-[9.5px] uppercase tracking-[0.07em] transition-colors ${
                  active
                    ? 'border-scout-gold/50 bg-parchment-faint text-parchment'
                    : 'border-divider bg-surface text-parchment-dim hover:border-scout-gold/30'
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>

        <span className="hidden flex-1 sm:block" />

        {/* Sort control — a mono segmented control. Default SIGNAL (high-signal first).
            Client-only re-order; the SSR HTML already renders SIGNAL order (crawlable). */}
        <div
          className="inline-flex flex-shrink-0 overflow-hidden rounded border-[0.5px] border-divider"
          role="group"
          aria-label="Sort the index"
        >
          {SORT_MODES.map(([mode, label], i) => {
            const active = sortMode === mode;
            return (
              <button
                key={mode}
                type="button"
                onClick={() => setSortMode(mode)}
                aria-pressed={active}
                className={`cursor-pointer px-2 py-1 font-mono text-[10px] uppercase tracking-[0.06em] transition-colors ${
                  i > 0 ? 'border-l-[0.5px] border-divider' : ''
                } ${
                  active
                    ? 'bg-scout-gold/10 text-scout-gold'
                    : 'bg-surface text-parchment-dim hover:text-parchment'
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* the index table */}
      {sorted.length > 0 ? (
        <div className="overflow-hidden">
          {/* desktop column header — hidden on phones (rows go two-line there). */}
          <div className="hidden grid-cols-[1fr_92px_84px_72px_92px_28px] items-center gap-3 border-b-[0.5px] border-divider px-3 py-2.5 font-mono text-[9px] uppercase tracking-[0.1em] text-slate sm:grid">
            <span>Entity</span>
            <span>Type</span>
            <span>Connections</span>
            <span>Intel</span>
            <span>Coverage</span>
            <span />
          </div>
          <ul className="flex flex-col">
            {sorted.map((e) => (
              <IndexRow
                key={e.slug}
                entity={e}
                edgesAdded30d={edgesAdded30d?.get(e.slug) ?? 0}
                onSelect={onSelect}
                active={e.slug === selectedSlug}
              />
            ))}
          </ul>
          {/* INTAKE DOOR (map-v2.html zone H) — a slim ghost row closing the index:
              the public "suggest a missing entity" door (graph_intake flag exists →
              /submit, the same destination the sidebar "Submit an entity" CTA uses). */}
          <IntakeDoorRow />
        </div>
      ) : (
        <>
          <p className="px-3 py-4 font-mono text-[13px] text-parchment-dim">
            {crossMatches.length > 0
              ? 'No entities match on this chain.'
              : 'No entities match this filter.'}
          </p>
          {/* Even with no matches, keep the door open (an empty index is exactly
              when "someone's missing" is most likely true). */}
          <IntakeDoorRow />
        </>
      )}

      {/* ── Cross-chain hint: dimmed "Other chains" rows (real /tags links) ── */}
      {crossMatches.length > 0 && (
        <div className="mt-6">
          <div className="mb-1 flex items-center gap-2.5 border-b-[0.5px] border-divider px-3 pb-2">
            <span className="font-mono text-[9px] uppercase tracking-[0.12em] text-slate">
              Other chains
            </span>
            <Mono size={10} color="var(--slate)">
              {crossMatches.length}
            </Mono>
          </div>
          <ul className="flex flex-col">
            {crossMatches.map((r) => (
              <li key={r.slug}>
                <Link
                  href={entityUrl(r.slug, { trailingSlash: false })}
                  className="flex items-center gap-3 border-b-[0.5px] border-divider px-3 py-2.5 opacity-60 transition-opacity hover:opacity-100"
                >
                  {r.image ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={r.image}
                      alt=""
                      loading="lazy"
                      className="h-5 w-5 flex-shrink-0 rounded object-cover"
                    />
                  ) : (
                    <Avatar name={r.name} type={r.type} size={20} />
                  )}
                  <span className="min-w-0 flex-1 truncate font-display text-[13.5px] font-semibold text-parchment">
                    {r.name}
                  </span>
                  <Mono size={10} color="var(--slate)">
                    {whereLabel(r)}
                  </Mono>
                  <span className="font-mono text-[9px] uppercase tracking-[0.07em] text-slate">
                    {ENTITY_TYPES[r.type as EntityTypeKey]?.label ?? r.type}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

/**
 * A single INDEX row (~44px on desktop, two-line on phones).
 *
 * Rendered as a real `<a href="/tags/[slug]">` (crawlable / SSR / middle-clickable),
 * but left-click is intercepted → selects into the unified inspector. Modified clicks
 * (cmd/ctrl/middle/shift) are LEFT ALONE so power-users can still open the canonical
 * glossary page in a new tab. Active row gets the inset gold spine (matches graph focus).
 */
function IndexRow({
  entity,
  edgesAdded30d = 0,
  onSelect,
  active,
}: {
  entity: ChainEntity;
  /** Typed edges added in the last 30d touching this entity (honest Δ30d). */
  edgesAdded30d?: number;
  onSelect: (slug: string) => void;
  active: boolean;
}) {
  const typeLabel = ENTITY_TYPES[entity.type as EntityTypeKey]?.label ?? entity.type;
  // Typed-edge degree (the graph's drawn edges). Absent/0 → em-dash (never "◆ 0").
  const connections = entity.connectionCount ?? 0;
  const intel = intelAgo(entity.recon?.generatedAt);
  const coverage = entity.contentCount ?? 0;

  const onClick = (ev: React.MouseEvent<HTMLAnchorElement>) => {
    // Respect modified clicks (open-in-new-tab etc.) — only intercept a plain left-click.
    if (ev.defaultPrevented || ev.button !== 0 || ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey) {
      return;
    }
    ev.preventDefault();
    onSelect(entity.slug);
  };

  return (
    <li>
      <a
        href={entityUrl(entity.slug, { trailingSlash: false })}
        onClick={onClick}
        aria-current={active ? 'true' : undefined}
        className={`group grid grid-cols-[1fr_auto] items-center gap-x-3 gap-y-1 border-b-[0.5px] border-divider px-3 py-2.5 transition-colors sm:grid-cols-[1fr_92px_84px_72px_92px_28px] sm:py-0 ${
          active
            ? 'bg-scout-gold/[0.08] shadow-[inset_2px_0_0_var(--amber)]'
            : 'hover:bg-parchment/[0.025]'
        }`}
        style={{ minHeight: 44 }}
      >
        {/* name cell (col 1) */}
        <span className="flex min-w-0 items-center gap-2.5">
          {entity.image ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={entity.image}
              alt=""
              loading="lazy"
              className="h-5 w-5 flex-shrink-0 rounded object-cover"
            />
          ) : (
            <Avatar name={entity.name} type={entity.type} size={20} />
          )}
          <span className="flex min-w-0 items-center gap-1.5">
            {entity.tracked && (
              <span
                className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-scout-gold shadow-[0_0_6px_var(--amber)]"
                aria-label="tracked"
              />
            )}
            <span className="truncate font-display text-[14px] font-semibold text-parchment">
              {entity.name}
            </span>
          </span>
        </span>

        {/* type chip — col 2 on desktop; rolls into the phone stat line (hidden here on phones). */}
        <span className="hidden sm:flex">
          <span className="rounded border-[0.5px] border-divider bg-surface px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.08em] text-parchment-dim">
            {typeLabel}
          </span>
        </span>

        {/* connections — col 3 (desktop only; phone shows it in the stat line below).
            Δ30d: a `+N` gold-mono marker when ≥1 typed edge was added in the last
            30d (real, from edge createdAt) — never an invented movement arrow. */}
        <span className="hidden items-center gap-1.5 font-mono text-[11px] sm:inline-flex">
          {connections > 0 ? (
            <span className="text-scout-gold">◆ {connections}</span>
          ) : (
            <span className="text-slate">—</span>
          )}
          {edgesAdded30d > 0 && (
            <span
              className="text-[9.5px] text-scout-gold/80"
              title={`${edgesAdded30d} new ${edgesAdded30d === 1 ? 'connection' : 'connections'} in the last 30 days`}
            >
              +{edgesAdded30d}
            </span>
          )}
        </span>

        {/* INTEL freshness — col 4 (desktop only). suppressHydrationWarning: the
            relative time is wall-clock-derived, so SSR "6m" can hydrate as "7m". */}
        <span suppressHydrationWarning className="hidden font-mono text-[11px] text-parchment-dim sm:inline">{intel}</span>

        {/* coverage — col 5 (desktop only) */}
        <span className="hidden font-mono text-[11px] sm:inline">
          {coverage > 0 ? (
            <span className="text-parchment-dim">
              <span className="text-parchment">{coverage}</span> {coverage === 1 ? 'story' : 'stories'}
            </span>
          ) : (
            <span className="text-slate">—</span>
          )}
        </span>

        {/* arrow — col 6 (desktop); on phones it pins to the right of the name row */}
        <span
          className={`flex justify-end text-slate transition-colors group-hover:text-parchment ${
            active ? 'text-scout-gold' : ''
          }`}
        >
          <Icon name="arrowUpRight" size={14} />
        </span>

        {/* ── phone-only second line: handle/type + the stats that are columns on desktop ── */}
        <span className="col-span-2 flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1 font-mono text-[10px] text-parchment-dim sm:hidden">
          <span className="inline-flex items-center gap-1 rounded border-[0.5px] border-divider bg-surface px-1.5 py-0.5 uppercase tracking-[0.08em]">
            {typeLabel}
          </span>
          {entity.handle && (
            <span className="inline-flex items-center gap-1 text-slate">
              <XIcon className="h-2.5 w-2.5 flex-shrink-0" />
              <span className="truncate">@{entity.handle}</span>
            </span>
          )}
          {connections > 0 && <span className="text-scout-gold">◆ {connections}</span>}
          {edgesAdded30d > 0 && <span className="text-scout-gold/80">+{edgesAdded30d}</span>}
          <span suppressHydrationWarning className="text-slate">{intel}</span>
          {coverage > 0 && (
            <span>
              <span className="text-parchment">{coverage}</span> {coverage === 1 ? 'story' : 'stories'}
            </span>
          )}
        </span>
      </a>
    </li>
  );
}

/**
 * INTAKE DOOR (map-v2.html zone H) — the slim dashed ghost row that closes THE
 * INDEX: a public door into the graph_intake suggest-an-entity flow. Links to
 * `/submit` (the SAME destination the dashboard sidebar's "Submit an entity"
 * CTA uses) — a missing-ENTITY suggestion has no in-page target slug, so the
 * per-entity GraphIntakeButton (which requires one) isn't the right tool here.
 */
function IntakeDoorRow() {
  return (
    <Link
      href="/submit"
      className="mt-3 flex items-center gap-3 rounded border border-dashed border-scout-gold/30 bg-scout-gold/[0.03] px-4 py-3 transition-colors hover:border-scout-gold/55 hover:bg-scout-gold/[0.06]"
    >
      <span className="flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center rounded-full border border-dashed border-scout-gold/40 text-scout-gold">
        <Icon name="plus" size={16} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block font-display text-[13.5px] font-bold text-parchment">
          See someone missing from the map?
        </span>
        <span className="mt-0.5 block font-mono text-[10px] tracking-[0.03em] text-parchment-dim">
          Suggest an entity or a connection — a human reviews every submission
          before the graph changes.
        </span>
      </span>
      <span className="flex-shrink-0 rounded bg-scout-gold px-3.5 py-2 font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-ink">
        Submit an entity
      </span>
    </Link>
  );
}
